import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import {defineCustomElements} from "@ionic/pwa-elements/loader";
import {Capacitor} from "@capacitor/core";
import * as Sentry from '@sentry/capacitor';
import { init as sentryAngularInit, browserTracingIntegration } from '@sentry/angular';

/**
 * Initialise Sentry error monitoring + performance tracing BEFORE Angular boots.
 * Gated on a non-empty DSN so an empty DSN (dev/test, or before a real DSN is
 * injected at build time) is a safe no-op. Wrapped in try/catch so any Sentry
 * failure can never block app boot.  Runs alongside Firebase Crashlytics.
 */
function initSentry(): void {
  const sentryConfig = environment.sentry;
  if (!sentryConfig?.dsn) {
    return;
  }
  try {
    Sentry.init(
      {
        dsn: sentryConfig.dsn,
        environment: sentryConfig.environment,
        release: sentryConfig.release,
        tracesSampleRate: sentryConfig.tracesSampleRate,
        dist: String(environment.buildNumber),
        integrations: [browserTracingIntegration()],
      },
      // The Angular sibling SDK's init is passed as the second arg so the
      // Capacitor layer wires up the Angular integrations correctly.
      sentryAngularInit,
    );

    // Any promise rejection that escapes Angular's zone still reaches Sentry.
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        Sentry.captureException(event.reason ?? event);
      } catch { /* monitoring must never throw */ }
    });
  } catch (err) {
    console.warn('Sentry initialisation failed:', err);
  }
}

initSentry();

if (!Capacitor.isNativePlatform()) {
  defineCustomElements(window);
}
if (environment.production) {
  enableProdMode();
}

if (Capacitor.isNativePlatform()) {
  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch((err: any) => console.log(err));
} else {
  platformBrowserDynamic().bootstrapModule(AppModule).then(() => {
    if ('serviceWorker' in navigator && environment.production) {
      navigator.serviceWorker.register('ngsw-worker.js');
    }
  }).catch(err => console.log(err));
}
